import { useQuery } from '@tanstack/react-query';
import {
  MAX_RUNNING_CAMPAIGNS_PER_PROJECT,
  assessBrandDnaForTemplateAi,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { Campaign } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { useOutreachReadiness } from './useOutreachReadiness';
import campStyles from './Campaigns.module.css';
import leadStyles from '@/features/leads/Leads.module.css';

type Props = {
  campaign: Campaign;
  projectId: string;
  runningCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  confirming?: boolean;
};

export function CampaignStartChecklistModal({
  campaign,
  projectId,
  runningCount,
  onConfirm,
  onCancel,
  confirming,
}: Props) {
  const readinessQ = useOutreachReadiness(projectId, campaign.send_rail === 'brevo_transactional' ? 'brevo_transactional' : 'smtp');

  const seqQ = useQuery({
    queryKey: ['sequence-detail', campaign.sequence_id],
    queryFn: () =>
      apiFetch<{ steps: unknown[] }>(`/api/sequences/${campaign.sequence_id}`),
  });

  const listQ = useQuery({
    queryKey: ['list-members-count', campaign.list_id],
    enabled: Boolean(campaign.list_id),
    queryFn: () =>
      apiFetch<{ lists: { id: string; member_count: number }[] }>(`/api/lists?project_id=${projectId}`),
  });

  const preflightQ = useQuery({
    queryKey: ['campaign-start-preflight', campaign.id],
    queryFn: () =>
      apiFetch<{
        preflight: {
          dnc_count: number;
          missing_legal_basis_count: number;
          can_start: boolean;
          block_reasons: string[];
          warnings: string[];
        };
      }>(`/api/campaigns/${campaign.id}/start-preflight`),
  });

  const brandQ = useQuery({
    queryKey: ['brand-dna', projectId],
    queryFn: () => apiFetch<{ brand_dna: Record<string, unknown> | null }>(`/api/projects/${projectId}/brand-dna`),
  });

  const dnaOk = assessBrandDnaForTemplateAi(brandQ.data?.brand_dna ?? null).ok;
  const stepCount = seqQ.data?.steps.length ?? 0;
  const memberCount =
    listQ.data?.lists.find((l) => l.id === campaign.list_id)?.member_count ?? 0;
  const readiness = readinessQ.data?.readiness;
  const maxRunningOk = runningCount < MAX_RUNNING_CAMPAIGNS_PER_PROJECT;

  const checks = [
    { ok: dnaOk, label: dnaOk ? 'Brand DNA compleet' : 'Brand DNA incompleet — vul merkprofiel aan' },
    { ok: readiness?.ready === true, label: readiness?.ready ? 'Mail/Brevo readiness OK' : 'Outreach readiness niet OK' },
    { ok: stepCount >= 1, label: stepCount >= 1 ? `Sequence: ${stepCount} stap(pen)` : 'Sequence heeft geen stappen' },
    { ok: memberCount > 0, label: memberCount > 0 ? `Lijst: ${memberCount} contacten` : 'Lijst is leeg' },
    { ok: maxRunningOk, label: maxRunningOk ? 'Running-limiet OK' : `Max ${MAX_RUNNING_CAMPAIGNS_PER_PROJECT} running campagnes bereikt` },
  ];

  const pre = preflightQ.data?.preflight;
  const legalChecks = [
    {
      ok: (pre?.dnc_count ?? 0) === 0,
      label:
        (pre?.dnc_count ?? 0) === 0
          ? 'Geen DNC-leads op lijst'
          : `Geblokkeerd: ${pre!.dnc_count} DNC-lead(s) op lijst`,
    },
    {
      ok: true,
      label:
        (pre?.missing_legal_basis_count ?? 0) === 0
          ? 'Legal basis OK (of niet verplicht)'
          : `Waarschuwing: ${pre!.missing_legal_basis_count} zonder legal_basis`,
    },
  ];

  const preflightReady = preflightQ.isLoading ? false : (pre?.can_start ?? false);
  const allOk = checks.every((c) => c.ok) && preflightReady;

  return (
    <div className={leadStyles.modalBackdrop} role="dialog" aria-modal="true" aria-labelledby="start-checklist-title">
      <div className={leadStyles.modal}>
        <h2 id="start-checklist-title" className={campStyles.panelTitle}>
          Checklist vóór start
        </h2>
        <p className={campStyles.panelBodyMuted}>Controleer outreach-voorwaarden voor &quot;{campaign.name}&quot;.</p>
        <ul className={campStyles.readinessList}>
          {checks.map((c) => (
            <li key={c.label} className={campStyles.readinessItem}>
              <span className={c.ok ? campStyles.readinessIconOk : campStyles.readinessIconPending}>
                {c.ok ? '✓' : '○'}
              </span>
              <span>{c.label}</span>
            </li>
          ))}
          {legalChecks.map((c) => (
            <li key={c.label} className={campStyles.readinessItem}>
              <span className={c.ok ? campStyles.readinessIconOk : campStyles.readinessIconPending}>
                {c.ok ? '✓' : '○'}
              </span>
              <span>{c.label}</span>
            </li>
          ))}
          {pre?.block_reasons.length ? (
            <li className={campStyles.readinessItem}>
              <span className={campStyles.readinessIconPending}>!</span>
              <span>{pre.block_reasons.join(' · ')}</span>
            </li>
          ) : null}
        </ul>
        <div className={leadStyles.modalActions}>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuleren
          </Button>
          <Button type="button" disabled={!allOk || confirming} onClick={onConfirm}>
            {confirming ? 'Starten…' : 'Start campagne'}
          </Button>
        </div>
      </div>
    </div>
  );
}
